import { useCallback, useMemo } from "react";
import { NumberParam, useQueryParams } from "use-query-params";

import {
  RYTE_DEBUG_FUTURE_HOURS_PARAM,
  RYTE_DEBUG_PAST_HOURS_PARAM,
  buildDebugTimeRangeResetUrl,
  buildDebugTimeRangeUrl,
  resolveRyteTimeRange,
  type RyteTimeRangeConfig,
} from "@/features/ryteTracker/config/debugTimeRange";
import { useDebugModeQuery } from "@/hooks/useDebugModeQuery";
import { useAppSelector } from "@/store/hooks";
import { isAllowListedAuthInfo } from "@/utils/allowListUtils";

import type { RootState } from "@/store/types";

interface UseRyteDebugTimeRangeQueryResult {
  range: RyteTimeRangeConfig;
  canConfigureRange: boolean;
  hasConfiguredRange: boolean;
  applyRange: (pastHours: number, futureHours: number) => void;
  resetRange: () => void;
  disableDebugMode: () => void;
}

const replaceCurrentLocation = (url: string): void => window.location.replace(url);
const selectAuthInfo = (state: RootState) => state.authInfo;

export const useRyteDebugTimeRangeQuery = (): UseRyteDebugTimeRangeQueryResult => {
  const { isDebugMode } = useDebugModeQuery();
  const authInfo = useAppSelector(selectAuthInfo);
  const canConfigureRange: boolean =
    process.env.NODE_ENV === "development" || isAllowListedAuthInfo(authInfo);
  const [params] = useQueryParams({
    [RYTE_DEBUG_PAST_HOURS_PARAM]: NumberParam,
    [RYTE_DEBUG_FUTURE_HOURS_PARAM]: NumberParam,
  });
  const pastHours = params[RYTE_DEBUG_PAST_HOURS_PARAM];
  const futureHours = params[RYTE_DEBUG_FUTURE_HOURS_PARAM];
  const range = useMemo(
    () =>
      resolveRyteTimeRange({
        isDebugMode: isDebugMode && canConfigureRange,
        pastHours,
        futureHours,
      }),
    [canConfigureRange, futureHours, isDebugMode, pastHours]
  );

  const applyRange = useCallback((past: number, future: number) => {
    replaceCurrentLocation(buildDebugTimeRangeUrl(window.location.href, past, future));
  }, []);
  const resetRange = useCallback(() => {
    replaceCurrentLocation(buildDebugTimeRangeResetUrl(window.location.href));
  }, []);
  const disableDebugMode = useCallback(() => {
    replaceCurrentLocation(buildDebugTimeRangeResetUrl(window.location.href, true));
  }, []);

  return {
    range,
    canConfigureRange,
    hasConfiguredRange: pastHours != null || futureHours != null,
    applyRange,
    resetRange,
    disableDebugMode,
  };
};
